// Account page - Wine POP combined preferences table
document.addEventListener('DOMContentLoaded', function() {
  const pathname = window.location.pathname;
  const isAccountPage = pathname === '/account' ||
                        pathname.match(/^\/[a-z]{2}\/account$/) ||
                        pathname.match(/^\/[a-z]{2}-[a-z]{2}\/account$/);

  if (!isAccountPage) {
    return;
  }

  const getCustomerId = () => {
    const metaTag = document.querySelector('meta[name="customer-id"]');
    return metaTag ? metaTag.getAttribute('content') : null;
  };

  const customerId = getCustomerId();
  if (!customerId) {
    return;
  }

  const getMetaContent = (name) => {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  };

  const buildVinpopAuthHeaders = () => {
    const timestamp = getMetaContent('friends-auth-timestamp');
    const signature = getMetaContent('friends-auth-signature');
    if (!customerId || !timestamp || !signature) {
      throw new Error('vinpop_auth_unavailable');
    }

    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': String(customerId),
      'X-Timestamp': timestamp,
      'X-Signature': signature
    };
  };

  const htmlDecodeTextarea = document.createElement('textarea');
  const decodeHtmlEntities = (value) => {
    let decoded = String(value || '');

    // Some product titles can be double-encoded (e.g. &amp;#39;), so decode repeatedly.
    for (let i = 0; i < 3; i += 1) {
      htmlDecodeTextarea.innerHTML = decoded;
      const nextValue = htmlDecodeTextarea.value;
      if (nextValue === decoded) {
        break;
      }
      decoded = nextValue;
    }

    return decoded;
  };

  const getLocale = () => {
    const htmlLang = document.documentElement.getAttribute('lang') || '';
    const shopifyLocale = window.Shopify && window.Shopify.locale ? window.Shopify.locale : '';
    return String(htmlLang || shopifyLocale || '').toLowerCase();
  };

  const getRatingLabels = () => {
    const locale = getLocale();
    if (locale.startsWith('nl')) {
      return {
        love: 'Ik hou ervan',
        like: 'Ik vind het lekker',
        dislike: 'Ik vind het niet lekker'
      };
    }

    return {
      love: decodeHtmlEntities("Love it"),
      like: decodeHtmlEntities("Like it"),
      dislike: decodeHtmlEntities("Don\u0026#39;t like it")
    };
  };

  loadCombinedPreferences();

  async function loadCombinedPreferences() {
    const container = document.getElementById('wine-preferences-container');
    if (!container) return;
    const ratedBottlesValue = document.getElementById('dashboard-rated-bottles-value');

    const productNameText = container.getAttribute('data-i18n-product-name') || 'Product Name';
    const yourRatingText = container.getAttribute('data-i18n-your-rating') || 'Your Rating';
    const noPreferencesText = container.getAttribute('data-i18n-no-preferences') || 'No preferences yet. Start rating wines to see them here!';

    let ratingsArray = [];
    let allProducts = [];

    try {
      const [ratingsResponse, productsResponse] = await Promise.all([
        fetch(`https://api.vinpop.fr/api/ratings/getAll?customerId=${customerId}`, { headers: buildVinpopAuthHeaders() }),
        fetch('/products.json?limit=250')
      ]);

      ratingsArray = await ratingsResponse.json();

      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        allProducts = productsData.products || [];
      }
    } catch (error) {
      console.error('Error loading account preferences:', error);
    }

    const normalizedRatings = Array.isArray(ratingsArray) ? ratingsArray : [];

    const uniqueRatedProductIds = new Set(
      normalizedRatings
        .filter((rating) => getRatingString(rating && rating.rating))
        .map((rating) => String(rating.id))
    );

    if (ratedBottlesValue) {
      ratedBottlesValue.textContent = String(uniqueRatedProductIds.size);
    }

    if (normalizedRatings.length === 0) {
      container.innerHTML = `<p style="color: rgba(var(--color-foreground), 0.75);">${noPreferencesText}</p>`;
      return;
    }

    const entries = normalizedRatings
      .map((rating) => ({
        productId: String(rating.id),
        rating: getRatingString(rating.rating)
      }))
      .filter((entry) => !!entry.rating);

    if (entries.length === 0) {
      container.innerHTML = `<p style="color: rgba(var(--color-foreground), 0.75);">${noPreferencesText}</p>`;
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'rated-wines-grid';
    grid.setAttribute('role', 'list');
    grid.setAttribute('aria-label', yourRatingText);

    for (const entry of entries) {
      const card = createRatedWineCard(entry, allProducts, {
        productNameText,
        yourRatingText
      });
      if (card) {
        grid.appendChild(card);
      }
    }

    container.innerHTML = '';
    container.appendChild(grid);
  }

  function getRatingString(num) {
    switch (String(num)) {
      case '3': return 'love';
      case '2': return 'like';
      case '1': return 'dislike';
      default: return null;
    }
  }

  function getProductInfo(productId, allProducts) {
    const normalizedId = String(productId);
    const shopifyProducts = window.shopifyProductData || {};
    let productInfo = shopifyProducts[normalizedId];

    const fallbackProduct = (allProducts || []).find((p) => p.id.toString() === normalizedId) ||
      (allProducts || []).find((p) => p.variants && p.variants.some((v) => v.id.toString() === normalizedId));

    if (!productInfo && fallbackProduct) {
      productInfo = {
        name: fallbackProduct.title,
        handle: fallbackProduct.handle,
        url: `/products/${fallbackProduct.handle}`,
        id: fallbackProduct.id,
        image: fallbackProduct.images && fallbackProduct.images.length > 0 ? fallbackProduct.images[0].src : null
      };
    }

    if (productInfo && !productInfo.image && fallbackProduct) {
      productInfo.image = fallbackProduct.images && fallbackProduct.images.length > 0
        ? fallbackProduct.images[0].src
        : null;
    }

    const resolvedName = productInfo ? decodeHtmlEntities(productInfo.name) : `Product ${normalizedId}`;

    return {
      name: resolvedName,
      url: productInfo ? productInfo.url : `/products/product-${normalizedId}`,
      image: productInfo && productInfo.image ? productInfo.image : null
    };
  }

  function createRatedWineCard(entry, allProducts, labels) {
    const productId = entry.productId;
    const productInfo = getProductInfo(productId, allProducts);

    const card = document.createElement('article');
    card.className = 'rated-wine-card text-scheme-text';
    card.setAttribute('role', 'listitem');
    card.setAttribute('data-product-id', productId);

    const mediaLink = document.createElement('a');
    mediaLink.href = productInfo.url;
    mediaLink.className = 'rated-wine-card__media';
    mediaLink.setAttribute('aria-label', productInfo.name);

    if (productInfo.image) {
      const image = document.createElement('img');
      image.src = productInfo.image;
      image.alt = productInfo.name;
      image.loading = 'lazy';
      image.width = 420;
      image.height = 420;
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'rated-wine-card__placeholder';
      placeholder.textContent = 'VIN POP';
      mediaLink.appendChild(placeholder);
    }

    const productLink = document.createElement('a');
    productLink.href = productInfo.url;
    productLink.className = 'rated-wine-card__title';
    productLink.textContent = productInfo.name;

    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'rated-wine-card__ratings product-rating__buttons';
    buttonsContainer.setAttribute('aria-label', labels.yourRatingText);

    const ratings = ['love', 'like', 'dislike'];
    const ratingEmojis = {
      love: '❤️',
      like: '👍',
      dislike: '👎'
    };
    const ratingLabels = getRatingLabels();

    ratings.forEach((type) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `rated-wine-card__rating-btn product-rating__btn push-btn push-btn--pop product-rating__btn--${type}`;
      button.setAttribute('data-rating', type);
      button.setAttribute('data-product-id', productId);
      button.setAttribute('aria-label', ratingLabels[type]);

      const isSelected = type === entry.rating;
      if (isSelected) {
        button.classList.add('selected');
      }

      const face = document.createElement('span');
      face.className = `push-btn__surface product-rating__btn-face product-rating__btn-face--${type}`;

      const emoji = document.createElement('span');
      emoji.className = 'product-rating__btn-emoji';
      emoji.textContent = ratingEmojis[type];

      const text = document.createElement('span');
      text.className = 'product-rating__btn-text';
      text.textContent = ratingLabels[type];

      face.appendChild(emoji);
      face.appendChild(text);
      button.appendChild(face);
      buttonsContainer.appendChild(button);

      button.addEventListener('click', async () => {
        await handleRatingClick(button, productId, type, buttonsContainer);
      });
    });

    card.appendChild(mediaLink);
    card.appendChild(productLink);
    card.appendChild(buttonsContainer);

    return card;
  }

  async function handleRatingClick(clickedButton, productId, rating, container) {
    // console.log(`🎯 Rating ${rating} clicked for product ${productId}`);

    const wasSelected = clickedButton.classList.contains('selected');

    // Remove selected class from all buttons in this container
    container.querySelectorAll('.product-rating__btn').forEach(btn => {
      btn.classList.remove('selected');
    });

    if (!wasSelected) {
      // Add new rating
      clickedButton.classList.add('selected');

      // Convert rating to number format
      const ratingNumber = rating === 'love' ? 3 : rating === 'like' ? 2 : 1;

      // Save to API
      await saveRating(productId, ratingNumber);

      // Pourquoi ce dislike : fenetre de vinpop-tracking.js, reponse envoyee au dashboard.
      if (rating === 'dislike' && window.VinPopTracking && typeof window.VinPopTracking.openDislikeReasons === 'function') {
        const titleEl = clickedButton.closest('.rated-wine-card, article, li')?.querySelector('.rated-wine-card__title');
        window.VinPopTracking.openDislikeReasons({
          productId,
          productTitle: titleEl ? titleEl.textContent.trim() : '',
          source: 'account'
        });
      }
    } else {
      // Remove rating
      await deleteRating(productId);
    }
  }

  async function saveRating(productId, rating) {
    const payload = {
      id: productId,
      customerId: customerId,
      rating: rating
    };

    try {
      const response = await fetch('https://api.vinpop.fr/api/ratings/save', {
        method: 'POST',
        headers: buildVinpopAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // console.log(`✅ Rating ${rating} saved for product ${productId}`);
      } else {
        console.error('❌ Failed to save rating');
      }
    } catch (error) {
      console.error('❌ Error saving rating:', error);
    }
  }

  async function deleteRating(productId) {
    try {
      const response = await fetch(`https://api.vinpop.fr/api/ratings/delete?customerId=${customerId}&id=${productId}`, {
        method: 'DELETE',
        headers: buildVinpopAuthHeaders()
      });

      if (response.ok) {
        // console.log(`✅ Rating deleted for product ${productId}`);
      } else {
        console.error('❌ Failed to delete rating');
      }
    } catch (error) {
      console.error('❌ Error deleting rating:', error);
    }
  }

});
